import useRequest from "@/lib/request/useRequest";
import { useMemo, useCallback, useEffect, useState, type ReactNode } from "react";
import { RxType } from "@/types/enums";
import { Rx } from "@/types/rx";
import { Feature } from "geojson";
import { SharedSelection } from "@heroui/react";
import {
    ColumnDef,
    SortingState,
    RowSelectionState,
} from "@tanstack/react-table";
import TableV2 from "@/components/table/TableV2";
import NumberInput from "@/components/inputs/NumberInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faDroplet,
    faDropletSlash,
    faCheck,
    faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { InferType, object, string, number, array } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { debounce } from "lodash";
import RxTableToolbar from "./RxTableToolbar";
import useAuth from "@/lib/auth/useAuth";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import { FertilizerType } from "@/types/products";
import { ImageryType } from "@/types/imagery";

const schema = object().shape({
    rate: string().oneOf(["n", "product"]).required(),
    rows: array()
        .of(
            object().shape({
                row: number().required(),
                target_rate_n: number().required(),
                product_rate: number().required(),
                actual_rate_n: number().optional(),
                actual_product_rate: number().optional(),
                rate: number().optional(),
            }),
        )
        .required(),
});
type FormType = InferType<typeof schema>;

const centeredHeader = (label: string) => () => (
    <span className="flex-1 text-center">{label}</span>
);

function CenteredCell({ children }: { children: ReactNode }) {
    return (
        <div className="flex size-full min-h-0 min-w-0 items-center justify-center px-1">
            {children}
        </div>
    );
}

/** Row id for TanStack selection — must stay in sync with `selectedFeatures` mapping below. */
function rxFeatureRowId(feature: Feature, index: number): string {
    if (feature.id !== undefined && feature.id !== null) {
        return String(feature.id);
    }
    return String(index);
}

const BAND_AVERAGE_HEADERS = {
    ["NDRE_HARMONIZED"]: "NDRE",
    [ImageryType.NDVI]: "NDVI",
    [ImageryType.CCCI]: "CCCI",
    [ImageryType.SAVI]: "SAVI",
    [ImageryType.OSAVI]: "OSAVI",
};
function RxTable({
    rx,
    onUpdate,
    fertilizerType,
}: {
    rx: Rx;
    onUpdate?: (updatedRx: Rx) => void;
    fertilizerType?: FertilizerType;
}) {
    const { t } = useTranslation();
    const { userIsSentinel } = useAuth();
    const { useMutateRequest } = useRequest();
    const updateRates = useMutateRequest<Rx>({
        url: `/prescriptions/${rx.id}/table/`,
        method: "put",
        successMessage: t("success.rates_updated_successfully", {
            ns: "messages",
        }),
        errorMessage: t("error.failed_to_update_rates", { ns: "messages" }),
    });
    const form = useForm<FormType>({
        resolver: yupResolver(schema),
        mode: "onBlur",
    });
    useEffect(() => {
        if (rx.table) {
            form.reset({
                rate: "n",
                rows: rx.table.features.map((feature: Feature) => ({
                    row: Number(feature?.id),
                    target_rate_n:
                        feature.properties?.target_rate_n?.toFixed(2),
                    product_rate: feature.properties?.product_rate?.toFixed(2),
                    actual_rate_n:
                        feature.properties?.actual_rate_n?.toFixed(2),
                    actual_product_rate:
                        feature.properties?.actual_product_rate?.toFixed(2),
                })),
            });
        }
    }, [rx.table, form]);

    const isBiomassProduct = useMemo(() => {
        return fertilizerType === FertilizerType.PlantGrowthRegulator || fertilizerType === FertilizerType.HarvestAid;
    }, [fertilizerType]);
    const zones = useMemo(() => {
        if (!rx.table) return [];
        return Array.from(
            new Set(
                rx.table.features
                    .map((feature: Feature) => feature.properties?.zone)
                    .filter((zone: number) => !!zone),
            ),
        ).sort((a: number, b: number) => a - b);
    }, [rx.table]);
    const treatments = useMemo(() => {
        if (!rx.table) return [];
        return Array.from(
            new Set(
                rx.table.features
                    .map((feature: Feature) => feature.properties?.treatment)
                    .filter((treatment: string) => !!treatment),
            ),
        ).sort((a: string, b: string) => a.localeCompare(b));
    }, [rx.table]);

    const { formatUnit } = useI18n();

    const handleUpdateSuccess = useCallback(
        (updatedRx: Rx) => {
            form.reset({
                rate: "n",
                rows: updatedRx.table.features.map((feature: Feature) => ({
                    row: Number(feature?.id),
                    target_rate_n: feature.properties?.target_rate_n,
                    product_rate: feature.properties?.product_rate,
                    actual_rate_n: feature.properties?.actual_rate_n,
                    actual_product_rate:
                        feature.properties?.actual_product_rate,
                })),
            });
            if (onUpdate) {
                onUpdate(updatedRx);
            }
        },
        [onUpdate, form],
    );
    const handleCellUpdate = debounce(
        (
            feature: Feature,
            columnKey:
                | "target_rate_n"
                | "product_rate"
                | "actual_rate_n"
                | "actual_product_rate",
        ) => {
            const values = form.getValues();
            const rateToUpdate =
                columnKey === "target_rate_n" || columnKey === "actual_rate_n"
                    ? "n"
                    : "product";
            const rate = Number(
                values.rows.find((row) => row.row === Number(feature.id))?.[
                columnKey
                ],
            );
            updateRates.mutate(
                {
                    data: {
                        rate: rateToUpdate,
                        rows: [
                            {
                                row: Number(feature.id),
                                rate: rate,
                            },
                        ],
                    },
                },
                {
                    onSuccess: (updatedRx: Rx) => {
                        handleUpdateSuccess(updatedRx);
                    },
                },
            );
        },
        500,
    );

    const renderCell = useCallback(
        (feature: Feature, columnKey: string) => {
            const value = feature.properties?.[columnKey];
            switch (columnKey) {
                case "target_rate_n":
                case "product_rate":
                case "actual_product_rate":
                    return (
                        <div
                            className="w-full min-w-0"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <NumberInput
                                name={`rows.${feature.id}.${columnKey}`}
                                variant="bordered"
                                size="sm"
                                onAutosave={() =>
                                    handleCellUpdate(feature, columnKey)
                                }
                                classNames={{
                                    input: "p-1 h-8",
                                    inputWrapper: "h-8 min-w-[80px]",
                                }}
                            />
                        </div>
                    );
                case "irrigated":
                    return feature.properties?.irrigated ? (
                        <FontAwesomeIcon
                            icon={faDroplet}
                            className="text-blue-500"
                        />
                    ) : (
                        <FontAwesomeIcon
                            icon={faDropletSlash}
                            className="text-gray-500"
                        />
                    );
                case "end_gun":
                    return feature.properties?.end_gun ? (
                        <FontAwesomeIcon
                            icon={faCheck}
                            className="text-green-500"
                        />
                    ) : (
                        <FontAwesomeIcon
                            icon={faXmark}
                            className="text-red-500"
                        />
                    );
                case "area":
                case "speed":
                case "depth":
                case "injection_rate":
                case "actual_rate_n":
                    return <span>{value?.toFixed(2)}</span>;
                default:
                    return <span>{value === null ? "-" : value}</span>;
            }
        },
        [handleCellUpdate],
    );

    const headerForProp = useCallback(
        (columnId: string) =>
            columnId === "product_rate_application" ||
                columnId === "actual_product_rate_diagnostic"
                ? "actual_product_rate"
                : columnId,
        [],
    );

    const tableColumns = useMemo<ColumnDef<Feature>[]>(() => {
        if (!rx.table) return [];

        const cols: ColumnDef<Feature>[] = [];
        const mk = (
            columnId: string,
            cellColumnKey: string,
            label: string,
            size = 100,
        ): ColumnDef<Feature> => ({
            id: columnId,
            accessorFn: (row) => {
                const propKey = headerForProp(columnId) as keyof NonNullable<
                    Feature["properties"]
                >;
                return row.properties?.[propKey];
            },
            header: centeredHeader(label),
            size,
            minSize: Math.min(96, Math.max(64, Math.floor(size * 0.65))),
            cell: ({ row }) => (
                <CenteredCell>
                    {renderCell(row.original, cellColumnKey)}
                </CenteredCell>
            ),
        });

        if (zones.length > 0) {
            cols.push(mk("zone", "zone", t("zone", { ns: "rx" })));
        }
        if (treatments.length > 0) {
            cols.push(mk("treatment", "treatment", t("treatment", { ns: "rx" })));
        }
        if (rx.rx_type === RxType.ZONE) {
            cols.push(
                mk("irrigated", "irrigated", t("irrigated", { ns: "rx" }), 72),
            )
            if (!isBiomassProduct) {
                cols.push(mk(
                    "target_rate_n",
                    "target_rate_n",
                    `${t("n_rate", { ns: "rx" })} (${formatUnit("lb_ac", "kg_ha")})`,
                    120,
                ));
            } else {
                cols.push(
                    {
                        id: "mean",
                        accessorFn: (row) => row.properties?.mean,
                        header: centeredHeader(
                            BAND_AVERAGE_HEADERS[rx.imagery_type as keyof typeof BAND_AVERAGE_HEADERS],
                        ),
                        size: 120,
                        cell: ({ row }) => (
                            <CenteredCell>
                                <span>
                                    {row.original.properties?.mean?.toFixed(3)}
                                </span>
                            </CenteredCell>
                        ),
                    },
                );
            }
            cols.push(mk(
                "product_rate",
                "product_rate",
                t("product_rate", { ns: "rx" }),
                120,
            ),
                mk("area", "area", t("area", { ns: "rx" })),
            );
        } else {
            cols.push(
                mk("start", "start", t("start", { ns: "rx" })),
                mk("stop", "stop", t("stop", { ns: "rx" })),
            )
            if (!isBiomassProduct) {
                cols.push(mk(
                    "target_rate_n",
                    "target_rate_n",
                    `${t("n_rate", { ns: "rx" })} (${formatUnit("lb_ac", "kg_ha")})`,
                    120,
                ));
            }
            cols.push(mk(
                "product_rate_application",
                "actual_product_rate",
                t("product_rate", { ns: "rx" }),
                120,
            ),
            );
            if (rx.rx_type === RxType.VRF) {
                cols.push(
                    mk(
                        "injection_rate",
                        "injection_rate",
                        t("injection_rate", { ns: "rx" }),
                    ),
                );
            } else if (rx.rx_type === RxType.VRI) {
                cols.push(
                    mk("speed", "speed", t("speed", { ns: "rx" })),
                    mk("depth", "depth", t("depth", { ns: "rx" })),
                );
            }
            cols.push(mk("end_gun", "end_gun", t("end_gun", { ns: "rx" }), 80));
            if (userIsSentinel) {
                cols.push(
                    mk(
                        "actual_rate_n",
                        "actual_rate_n",
                        t("actual_rate_n", { ns: "rx" }),
                        120,
                    ),
                    mk(
                        "actual_product_rate_diagnostic",
                        "actual_product_rate",
                        t("actual_product_rate", { ns: "rx" }),
                        120,
                    ),
                );
            }
        }
        return cols;
    }, [
        rx.table,
        rx.rx_type,
        zones.length,
        treatments.length,
        t,
        formatUnit,
        userIsSentinel,
        renderCell,
        headerForProp,
        isBiomassProduct,
        rx.imagery_type,
    ]);

    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [zoneFilter, setZoneFilter] = useState<SharedSelection>("all");
    const [treatmentFilter, setTreatmentFilter] =
        useState<SharedSelection>("all");
    const [irrigatedFilter, setIrrigatedFilter] =
        useState<SharedSelection>("all");
    const [endGunFilter, setEndGunFilter] = useState<SharedSelection>("all");
    const filteredItems = useMemo(() => {
        if (!rx.table?.features) return [];
        let filteredFeatures = rx.table.features;
        if (
            zoneFilter !== "all" &&
            Array.from(zoneFilter).length !== zones.length
        ) {
            filteredFeatures = filteredFeatures.filter((feature: Feature) => {
                return Array.from(zoneFilter).includes(
                    feature.properties?.zone?.toString(),
                );
            });
        }
        if (
            treatmentFilter !== "all" &&
            Array.from(treatmentFilter).length !== treatments.length
        ) {
            filteredFeatures = filteredFeatures.filter((feature: Feature) => {
                return Array.from(treatmentFilter).includes(
                    feature.properties?.treatment,
                );
            });
        }
        if (irrigatedFilter !== "all") {
            filteredFeatures = filteredFeatures.filter((feature: Feature) => {
                return Array.from(irrigatedFilter).includes(
                    feature.properties?.irrigated?.toString(),
                );
            });
        }
        if (endGunFilter !== "all") {
            const value = Array.from(endGunFilter).includes("true");
            filteredFeatures = filteredFeatures.filter((feature: Feature) => {
                return feature.properties?.end_gun === value;
            });
        }
        return filteredFeatures;
    }, [
        rx.table?.features,
        zoneFilter,
        treatmentFilter,
        irrigatedFilter,
        endGunFilter,
        zones.length,
        treatments.length,
    ]);
    const [sorting, setSorting] = useState<SortingState>(() => [
        {
            id:
                zones.length > 0
                    ? "zone"
                    : rx.rx_type !== RxType.ZONE
                        ? "stop"
                        : "target_rate_n",
            desc: false,
        },
    ]);

    const sortedItems = useMemo(() => {
        const primary = sorting[0];
        if (!primary?.id) return filteredItems;

        const column = headerForProp(primary.id);
        const direction = primary.desc ? "descending" : "ascending";

        const sorted = [...filteredItems].sort((a: Feature, b: Feature) => {
            const aValue = a.properties?.[column];
            const bValue = b.properties?.[column];

            switch (column) {
                case "irrigated":
                case "end_gun":
                    return direction === "ascending"
                        ? aValue === bValue
                            ? 0
                            : aValue
                                ? -1
                                : 1
                        : aValue === bValue
                            ? 0
                            : aValue
                                ? 1
                                : -1;

                case "zone":
                    return direction === "ascending"
                        ? Number(aValue) - Number(bValue)
                        : Number(bValue) - Number(aValue);

                default:
                    if (
                        typeof aValue === "number" &&
                        typeof bValue === "number"
                    ) {
                        return direction === "ascending"
                            ? aValue - bValue
                            : bValue - aValue;
                    }

                    if (aValue === bValue) return 0;
                    if (aValue === undefined || aValue === null) {
                        return direction === "ascending" ? -1 : 1;
                    }
                    if (bValue === undefined || bValue === null) {
                        return direction === "ascending" ? 1 : -1;
                    }
                    return direction === "ascending"
                        ? String(aValue).localeCompare(String(bValue))
                        : String(bValue).localeCompare(String(aValue));
            }
        });
        return sorted;
    }, [filteredItems, sorting, headerForProp]);

    const selectedFeatures = useMemo((): SharedSelection => {
        const selectedKeys = new Set(
            Object.keys(rowSelection).filter((id) => rowSelection[id]),
        );
        if (selectedKeys.size === 0) return new Set();

        const out = new Set<string | number>();
        sortedItems.forEach((feature, index) => {
            if (selectedKeys.has(rxFeatureRowId(feature, index))) {
                if (feature.id !== undefined && feature.id !== null) {
                    out.add(feature.id);
                }
            }
        });
        return out;
    }, [rowSelection, sortedItems]);

    const topContent = useMemo(() => {
        return (
            <RxTableToolbar
                rxId={rx.id}
                zones={zones}
                treatments={treatments}
                rxType={rx.rx_type}
                zoneFilter={zoneFilter}
                onZoneFilterChange={setZoneFilter}
                treatmentFilter={treatmentFilter}
                onTreatmentFilterChange={setTreatmentFilter}
                irrigatedFilter={irrigatedFilter}
                onIrrigatedFilterChange={setIrrigatedFilter}
                endGunFilter={endGunFilter}
                onEndGunFilterChange={setEndGunFilter}
                table={rx.table}
                plots={rx.plots}
                selectedFeatures={selectedFeatures}
                filteredItems={filteredItems}
                updateRatesRequest={updateRates}
                handleUpdateSuccess={handleUpdateSuccess}
                isBiomassProduct={isBiomassProduct}
            />
        );
    }, [
        rx.id,
        rx.rx_type,
        rx.table,
        rx.plots,
        zones,
        treatments,
        zoneFilter,
        treatmentFilter,
        irrigatedFilter,
        endGunFilter,
        selectedFeatures,
        filteredItems,
        updateRates,
        handleUpdateSuccess,
        isBiomassProduct,
    ]);

    return (
        <FormProvider {...form}>
            <form className="flex min-h-0 w-full min-w-0 flex-col gap-2">
                {topContent}
                <div className="h-[min(70vh,720px)] min-h-[220px] w-full min-w-0 overflow-hidden">
                    <TableV2
                        data={sortedItems}
                        columns={tableColumns}
                        sorting={sorting}
                        setSorting={setSorting}
                        manualSorting
                        rowSelection={rowSelection}
                        onRowSelectionChange={setRowSelection}
                        getRowId={rxFeatureRowId}
                        estimateSize={52}
                        showScrollbar
                        fillWidthIfPossible
                    />
                </div>
            </form>
        </FormProvider>
    );
}

export default RxTable;
